import { PlasticsCalendarEvent } from './../models/plastics-calendar-event.model';
import { PlasticsCalendarEventType } from '../enums/plastics-calendar-event-type.enum';
import { stripTime } from './strip-time.function';
/**
 * Converts working days to calendar days, skipping weekends (Saturdays and Sundays).
 * Starts counting from the day after (current date + cumulativeDays).
 * Handles fractional working days by requiring full weekdays.
 * Does not account for holidays.
 *
 * @param workingDays Number of working days to convert (>0; can be fractional).
 * @param cumulativeDays Optional calendar day offset from today (default: 0).
 * @returns Calendar days needed, including weekends.
 *
 * @example
 * ```ts
 * // On Sept 9, 2025 (Tuesday):
 * workingDaysToCalendarDays(1); // 1 (Sept 10, Wed)
 * workingDaysToCalendarDays(5); // 7 (Sept 10-12, 15-16)
 * workingDaysToCalendarDays(2.5); // 3 (Sept 10-12)
 * workingDaysToCalendarDays(2, 3); // 4 (Sept 15-16, after offset to Sept 12)
 * ```
 */
export function workingDaysToCalendarDays(workingDays: number, cumulativeDays: number = 0): number {
	let calendarDays = 0;
	let daysRemaining = workingDays;
	const currentDate = new Date();
	currentDate.setDate(currentDate.getDate() + cumulativeDays)

	while (daysRemaining > 0) {
		const day = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
		if (day !== 0 && day !== 6) { // only count weekdays
			daysRemaining--;
		}
		calendarDays++;
		currentDate.setDate(currentDate.getDate() + 1);
	}

	return calendarDays;
}

/**
 * Note: eventsByDate must be a record created by ```timeStrippedDateISOToPlasticsCalendarEventsDict``` prior to calling this function to preserve time complexity,
 * @param workingDays number of working days to convert
 * @param cumulativeDays calendar day offset from today
 * @param eventsByDate a map of stripped time date ISO strings to calendar events
 * @returns Calendar days needed, accounting for weekends, holidays, and overtime.
 * @see timeStrippedDateISOToPlasticsCalendarEventsDict()
 * 
 * @example
 * ```ts
 * const workingDays: 15;
 * const cumulativeDays: 3;
 * const events: PlasticsCalendarEvent[] = queriedEvents;
 * const eventsByDate: Record<string, PlasticsCalendarEvent[]> = timeStrippedDateISOToPlasticsCalendarEventsDict(events)
 * const calendarDays = workingDaysToCalendarDaysAccountingForEvents(workingDays, cumulativeDays, eventsByDate);
 * ```
 */
export function workingDaysToCalendarDaysAccountingForEvents(
	workingDays: number,
	cumulativeDays: number,
	eventsByDate: Record<string, PlasticsCalendarEvent[]>,
	startDate: Date = new Date(),
): number {
	if (workingDays <= 0) return 0;

	let calendarDays = 0;
	let daysRemaining = workingDays;
	const currentDate = new Date(startDate);
	currentDate.setDate(currentDate.getDate() + cumulativeDays);
	let overtimeShiftCount = 0;

	while (daysRemaining > 0) {
		const dayKey = stripTime(currentDate).toISOString();
		const dayEvents: PlasticsCalendarEvent[] = eventsByDate[dayKey] ?? [];

		let hasHoliday = false;
		let dayOvertimeShifts = 0;
		for (const ev of dayEvents) {
			if (ev.type === PlasticsCalendarEventType.Holiday) {
				hasHoliday = true;
			} else if (ev.type === PlasticsCalendarEventType.Overtime) {
				dayOvertimeShifts++;
			}
		}
		overtimeShiftCount += dayOvertimeShifts;

		const day = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
		const isWorkingDay = (day !== 0 && day !== 6) && !hasHoliday;
		if (isWorkingDay) {
			daysRemaining -= 1;
		}

		calendarDays++;
		currentDate.setDate(currentDate.getDate() + 1);
	}

	const overtimeCount = Math.round(overtimeShiftCount / 3);

	return calendarDays - overtimeCount;
}


export function timeStrippedDateISOToPlasticsCalendarEventsDict(events: PlasticsCalendarEvent[]) {
	const eventsByDate: Record<string, PlasticsCalendarEvent[]> = {};
	for (const event of events) {
		const key = stripTime(event.eventDate).toISOString();
		if (!eventsByDate[key]) eventsByDate[key] = [];
		eventsByDate[key].push(event);
	}

	return eventsByDate
}
